'use client';

import { Modal } from './Modal';
import { Button } from './Button';
import { AlertCircle, Clock } from 'lucide-react';

interface TokenLimitModalProps {
  isOpen: boolean;
  onClose: () => void;
  used: number;
  limit: number;
}

function formatNumber(num: number): string {
  return num.toLocaleString();
}

function getResetTimeLocal(): string {
  // Get tomorrow at midnight UTC
  const now = new Date();
  const tomorrowUTC = new Date(Date.UTC(
    now.getUTCFullYear(),
    now.getUTCMonth(),
    now.getUTCDate() + 1,
    0, 0, 0
  ));

  // Format in user's local timezone with timezone name
  return tomorrowUTC.toLocaleTimeString(undefined, {
    hour: 'numeric',
    minute: '2-digit',
    timeZoneName: 'short',
  });
}

export function TokenLimitModal({ isOpen, onClose, used, limit }: TokenLimitModalProps) {
  const percentage = Math.min(100, Math.round((used / limit) * 100));
  const resetTime = getResetTimeLocal();

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Daily Limit Reached">
      <div className="text-center">
        <div className="mx-auto mb-4 w-16 h-16 rounded-full bg-amber-900/50 flex items-center justify-center">
          <AlertCircle className="w-8 h-8 text-amber-500" />
        </div>

        <h3 className="text-lg font-semibold text-[var(--color-text)] mb-2">
          You&apos;ve reached your daily AI limit
        </h3>
        <p className="text-[var(--color-text-secondary)] mb-6">
          To help manage costs, there&apos;s a daily limit on AI-powered features like article summaries.
        </p>

        <div className="bg-[var(--color-bg)] rounded-lg p-4 mb-6">
          <div className="flex justify-between text-sm mb-2">
            <span className="text-[var(--color-text-secondary)]">Today&apos;s usage</span>
            <span className="text-[var(--color-text)] font-medium">{percentage}%</span>
          </div>
          <div className="w-full bg-[var(--color-surface-hover)] rounded-full h-2 mb-3">
            <div
              className="bg-amber-500 h-2 rounded-full transition-all"
              style={{ width: `${percentage}%` }}
            />
          </div>
          <div className="flex justify-between text-xs text-[var(--color-text-muted)]">
            <span>{formatNumber(used)} tokens used</span>
            <span>{formatNumber(limit)} limit</span>
          </div>
        </div>

        <div className="flex items-center justify-center gap-2 text-sm text-[var(--color-text-secondary)] mb-6">
          <Clock size={16} />
          <span>Resets at {resetTime}</span>
        </div>

        <Button onClick={onClose} className="w-full">
          Got it
        </Button>
      </div>
    </Modal>
  );
}
